
import React, { useMemo } from 'react';
import { Puzzle } from '../types';
import PostCard from '../components/PostCard';
import { useAuth } from '../AuthContext';
import { usePuzzles } from '../PuzzleContext';
import BackButton from '../components/BackButton';

interface PostDetailPageProps {
    puzzle: Puzzle;
}

const PostDetailPage: React.FC<PostDetailPageProps> = ({ puzzle: initialPuzzle }) => {
    const { userProfile } = useAuth();
    const { puzzles, reportPuzzle } = usePuzzles();

    // Find the live version of this puzzle from the context to ensure sync
    const livePuzzle = useMemo(() => {
        if (!initialPuzzle) return undefined;
        const found = puzzles.find(p => p.id === initialPuzzle.id);
        if (found) return found;
        // If it has creatorId, it's a full puzzle object, so we can safely use it
        if ('creatorId' in initialPuzzle) return initialPuzzle;
        return undefined;
    }, [puzzles, initialPuzzle]);

    const suggestions = useMemo(() => {
        if (!userProfile || !livePuzzle) return [];

        // Prioritize puzzles from friends
        const friendsPuzzles = puzzles.filter(p =>
            p.id !== livePuzzle.id &&
            !userProfile.solvedPuzzles.includes(p.id) &&
            p.creatorId !== userProfile.uid &&
            userProfile.following.includes(p.creatorId)
        );

        // Get other puzzles (not from friends)
        const otherPuzzles = puzzles.filter(p =>
            p.id !== livePuzzle.id &&
            !userProfile.solvedPuzzles.includes(p.id) &&
            p.creatorId !== userProfile.uid &&
            !userProfile.following.includes(p.creatorId)
        );
        
        // Combine, prioritizing friends' puzzles, and take the first two
        return [...friendsPuzzles, ...otherPuzzles].slice(0, 2);
    }, [livePuzzle, userProfile, puzzles]);

    if (!livePuzzle) {
        return (
            <div className="space-y-4">
                <BackButton />
                <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
                    <i className="ph-duotone ph-ghost text-6xl text-gray-300 mb-4"></i>
                    <h2 className="text-xl font-bold text-gray-700 mb-2">Post Not Found</h2>
                    <p className="text-gray-500">This post may have been deleted or is no longer available.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <BackButton />
            
            <PostCard 
                puzzle={livePuzzle} 
                isClickable={false} 
                showCommentsInitially={true}
                suggestions={suggestions}
                onReport={reportPuzzle}
            />
        </div>
    );
};

export default PostDetailPage;
